/**
 * Time Utility Functions
 * Provides consistent time handling across the application
 * - Display: 12-hour format (e.g., "10:00 PM")
 * - Backend/Storage: 24-hour format (e.g., "22:00")
 */

/**
 * Converts 24-hour time string to 12-hour format
 * @param {string} time24 - Time in HH:mm or HH:mm:ss format (e.g., "14:30", "22:00")
 * @returns {string} Time in 12-hour format (e.g., "2:30 PM", "10:00 PM")
 */
export const format24To12Hour = (time24) => {
    if (!time24) return '';
    
    const [hourStr, minuteStr] = String(time24).split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || '00';
    
    if (isNaN(hour)) return time24;
    
    const period = hour >= 12 ? 'PM' : 'AM';
    
    // Convert 24-hour to 12-hour
    if (hour === 0) {
        hour = 12; // Midnight
    } else if (hour > 12) {
        hour = hour - 12;
    }
    
    return `${hour}:${minute} ${period}`;
};

/**
 * Converts 12-hour time string to 24-hour format
 * @param {string} time12 - Time in 12-hour format (e.g., "2:30 PM", "10:00 PM")
 * @returns {string} Time in HH:mm format (e.g., "14:30", "22:00")
 */
export const format12To24Hour = (time12) => {
    if (!time12) return '00:00';
    
    const timeStr = String(time12).trim().toUpperCase();
    const isPM = timeStr.includes('PM');
    const isAM = timeStr.includes('AM');
    
    // Remove AM/PM and extract time parts
    const timePart = timeStr.replace(/AM|PM/gi, '').trim();
    const [hourStr, minuteStr] = timePart.split(':');
    
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr ? minuteStr.padStart(2, '0') : '00';
    
    if (isNaN(hour)) return '00:00';
    
    // Convert to 24-hour format
    if (isPM && hour !== 12) {
        hour += 12;
    } else if (isAM && hour === 12) {
        hour = 0; // Midnight
    }
    
    return `${String(hour).padStart(2, '0')}:${minute}`;
};

/**
 * Normalizes time input to HH:mm format
 * Handles various input formats including 24:00
 * @param {string} time - Time string in various formats
 * @returns {string} Normalized time in HH:mm format
 */
export const normalizeTime = (time) => {
    if (!time) return '00:00';
    
    const timeStr = String(time).trim();
    
    // Check if it's 12-hour format (contains AM/PM)
    if (timeStr.toUpperCase().includes('AM') || timeStr.toUpperCase().includes('PM')) {
        return format12To24Hour(timeStr);
    }
    
    // Handle 24-hour format
    // Special case: 24:00 represents end of day (midnight of next day)
    if (timeStr === '24:00' || timeStr === '24:00:00') {
        return '00:00';
    }
    
    const parts = timeStr.split(':');
    let hour = parseInt(parts[0], 10) || 0;
    const minute = parseInt(parts[1], 10) || 0;
    
    // Clamp to valid range
    if (hour >= 24) hour = 0;
    if (hour < 0) hour = 0;
    
    const hh = String(hour).padStart(2, '0');
    const mm = String(minute).padStart(2, '0');
    
    return `${hh}:${mm}`;
};

/**
 * Checks if a time range wraps past midnight
 * @param {string} startTime - Start time in HH:mm format
 * @param {string} endTime - End time in HH:mm format
 * @returns {boolean} True if the range wraps past midnight
 */
export const isTimeRangeWrappingMidnight = (startTime, endTime) => {
    if (!startTime || !endTime) return false;
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    return startMinutes >= endMinutes;
};

/**
 * Formats a time range for display
 * @param {string} startTime - Start time in HH:mm format
 * @param {string} endTime - End time in HH:mm format
 * @param {boolean} use12Hour - Use 12-hour format (default: true)
 * @returns {string} Formatted time range (e.g., "10:00 PM - 11:00 PM")
 */
export const formatTimeRange = (startTime, endTime, use12Hour = true) => {
    if (!startTime || !endTime) return '';
    
    if (use12Hour) {
        return `${format24To12Hour(startTime)} - ${format24To12Hour(endTime)}`;
    }
    
    return `${startTime} - ${endTime}`;
};

/**
 * Validates if a time string is in valid HH:mm format
 * @param {string} time - Time string to validate
 * @returns {boolean} True if valid
 */
export const isValidTimeFormat = (time) => {
    if (!time) return false;
    
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    return timeRegex.test(time);
};

/**
 * Creates a time input value for HTML input[type="time"]
 * @param {string} time - Time in any format
 * @returns {string} Time in HH:mm format suitable for input
 */
export const getTimeInputValue = (time) => {
    return normalizeTime(time);
};

/**
 * Parses time from HTML input[type="time"]
 * @param {string} inputValue - Value from time input
 * @returns {string} Normalized time in HH:mm format
 */
export const parseTimeInput = (inputValue) => {
    return normalizeTime(inputValue);
};

/**
 * Converts time to minutes since midnight for easier comparison
 * @param {string} time - Time in HH:mm format
 * @returns {number} Minutes since midnight
 */
export const timeToMinutes = (time) => {
    if (!time) return 0;
    
    const [hour, minute] = time.split(':').map(Number);
    return (hour || 0) * 60 + (minute || 0);
};

/**
 * Checks if two time ranges overlap
 * Handles midnight wrapping scenarios
 * @param {string} start1 - First range start time (HH:mm)
 * @param {string} end1 - First range end time (HH:mm)
 * @param {string} start2 - Second range start time (HH:mm)
 * @param {string} end2 - Second range end time (HH:mm)
 * @param {boolean} wrap1 - Whether first range wraps midnight
 * @param {boolean} wrap2 - Whether second range wraps midnight
 * @returns {boolean} True if ranges overlap
 */
export const doTimeRangesOverlap = (start1, end1, start2, end2, wrap1 = false, wrap2 = false) => {
    if (!start1 || !end1 || !start2 || !end2) return false;
    
    const s1 = timeToMinutes(start1);
    const e1 = timeToMinutes(end1);
    const s2 = timeToMinutes(start2);
    const e2 = timeToMinutes(end2);
    
    // Handle non-wrapping ranges
    if (!wrap1 && !wrap2) {
        // Standard overlap check: ranges overlap if one starts before the other ends
        return !(e1 <= s2 || e2 <= s1);
    }
    
    // Handle wrapping ranges (spans midnight)
    // Wrapping range example: 22:00 - 02:00 means 22:00-23:59 and 00:00-02:00
    
    if (wrap1 && !wrap2) {
        // Range1 wraps midnight, Range2 doesn't
        // Range1 covers: [s1, 24:00) and [00:00, e1)
        // Check if Range2 overlaps with either part
        const overlapsAfterMidnight = !(e1 <= s2 || e2 <= 0);
        const overlapsBeforeMidnight = !(1440 <= s2 || e2 <= s1);
        return overlapsAfterMidnight || overlapsBeforeMidnight;
    }
    
    if (!wrap1 && wrap2) {
        // Range2 wraps midnight, Range1 doesn't
        const overlapsAfterMidnight = !(e2 <= s1 || e1 <= 0);
        const overlapsBeforeMidnight = !(1440 <= s1 || e1 <= s2);
        return overlapsAfterMidnight || overlapsBeforeMidnight;
    }
    
    // Both wrap midnight - they will always overlap
    return true;
};

/**
 * Checks if two day arrays have any common days
 * @param {number[]} days1 - First array of day numbers (1-7)
 * @param {number[]} days2 - Second array of day numbers (1-7)
 * @returns {boolean} True if arrays share any days
 */
export const doDaysOverlap = (days1, days2) => {
    if (!days1 || !days2 || days1.length === 0 || days2.length === 0) return false;
    return days1.some(day => days2.includes(day));
};

/**
 * Parses CSV string of days to array of numbers
 * @param {string} daysCsv - CSV string like "1,2,3,4,5"
 * @returns {number[]} Array of day numbers
 */
export const parseDaysCsv = (daysCsv) => {
    if (!daysCsv) return [];
    return daysCsv.split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n) && n >= 1 && n <= 7);
};

/**
 * Formats array of day numbers to readable string
 * @param {number[]} days - Array of day numbers (1-7)
 * @returns {string} Formatted day string (e.g., "Mon, Tue, Wed")
 */
export const formatDaysArray = (days) => {
    if (!days || days.length === 0) return '';
    
    const dayNames = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => dayNames[day] || '?').join(', ');
};

/**
 * Validates if a pricing rule overlaps with existing rules
 * @param {Object} newRule - New rule to validate (with 12-hour format times)
 * @param {Array} existingRules - Array of existing rules (with 24-hour format times from backend)
 * @param {number[]} selectedDays - Array of selected day numbers
 * @returns {Object} Validation result with overlap info
 */
export const validatePricingRuleOverlap = (newRule, existingRules, selectedDays) => {
    const result = {
        hasOverlap: false,
        overlappingRules: [],
        message: ''
    };
    
    if (!newRule || !existingRules || existingRules.length === 0) {
        return result;
    }
    
    // Convert new rule times to 24-hour format
    const newStartTime = format12To24Hour(newRule.startTime || '00:00 AM');
    const newEndTime = format12To24Hour(newRule.endTime || '11:59 PM');
    const newDays = selectedDays || parseDaysCsv(newRule.daysOfWeekCsv);
    const newWrapsMidnight = newRule.wrapsMidnight || isTimeRangeWrappingMidnight(newStartTime, newEndTime);
    
    // Check each existing rule for overlaps
    existingRules.forEach(existingRule => {
        // Skip the same rule if editing
        if (existingRule.id && newRule.id && existingRule.id === newRule.id) {
            return;
        }
        
        // Skip inactive rules
        if (!existingRule.active) {
            return;
        }
        
        const existingStartTime = existingRule.startTime;
        const existingEndTime = existingRule.endTime;
        const existingDays = parseDaysCsv(existingRule.daysOfWeekCsv);
        const existingWrapsMidnight = existingRule.wrapsMidnight;
        
        // Check if days overlap
        if (doDaysOverlap(newDays, existingDays)) {
            // Check if time ranges overlap
            if (doTimeRangesOverlap(newStartTime, newEndTime, existingStartTime, existingEndTime, newWrapsMidnight, existingWrapsMidnight)) {
                result.hasOverlap = true;
                const commonDays = newDays.filter(day => existingDays.includes(day));
                result.overlappingRules.push({
                    id: existingRule.id,
                    name: existingRule.name || `Rule ${existingRule.id}`,
                    days: formatDaysArray(commonDays),
                    timeRange: formatTimeRange(existingStartTime, existingEndTime),
                    wrapsMidnight: existingWrapsMidnight,
                    fixedAmount: existingRule.fixedAmount
                });
            }
        }
    });
    
    // Generate warning message
    if (result.hasOverlap) {
        result.message = `⚠️ This rule overlaps with ${result.overlappingRules.length} existing rule(s)`;
    }
    
    return result;
};
